import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { checkRateLimit, logSecurityEvent } from '@/lib/security';
import { Mail, Gift, CheckCircle, Download } from 'lucide-react';

interface EmailCaptureProps {
  variant?: 'default' | 'hero' | 'sidebar' | 'popup';
  leadMagnet?: string;
  source?: string;
  title?: string;
  description?: string;
  className?: string;
}

export const EmailCapture: React.FC<EmailCaptureProps> = ({
  variant = 'default',
  leadMagnet = 'event_planning_checklist',
  source = 'landing_page',
  title,
  description,
  className = ''
}) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { toast } = useToast();

  const leadMagnets = {
    event_planning_checklist: {
      title: 'Free Event Planning Checklist',
      description: 'Get our comprehensive 25-point checklist that ensures your events run smoothly every time.',
      icon: CheckCircle
    },
    date_coordination_guide: {
      title: 'Ultimate Date Coordination Guide',
      description: 'Learn the secrets to coordinating dates with groups of 10+ people without the back-and-forth chaos.',
      icon: Download
    },
    event_templates: {
      title: 'Ready-to-Use Event Templates',
      description: 'Download 5 proven event templates for meetings, parties, and corporate events.',
      icon: Gift
    }
  };

  const currentMagnet = leadMagnets[leadMagnet as keyof typeof leadMagnets] || leadMagnets.event_planning_checklist;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Email required",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Rate limiting check for email subscriptions
      const rateLimitPassed = await checkRateLimit('email_subscription')
      if (!rateLimitPassed) {
        toast({
          title: "Rate limit exceeded",
          description: "Please wait before subscribing again.",
          variant: "destructive",
        })
        return
      }

      const trimmedEmail = email.toLowerCase().trim()
      const trimmedName = name.trim() || null

      const { error } = await supabase
        .from('email_subscribers')
        .insert({
          email: trimmedEmail,
          name: trimmedName,
          source,
          lead_magnet: leadMagnet,
        });

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          toast({
            title: "Already subscribed!",
            description: "You're already on our list. Check your email for the download link.",
          });
          
          // Log duplicate subscription attempt
          await logSecurityEvent('duplicate_email_subscription', {
            email: trimmedEmail,
            source,
            leadMagnet
          })
        } else {
          throw error;
        }
      } else {
        setIsSubscribed(true);
        toast({
          title: "Success! 🎉",
          description: "Thanks for subscribing! We'll be in touch soon with your free resources.",
        });

        // Log successful subscription
        await logSecurityEvent('email_subscription_success', {
          source,
          leadMagnet,
          hasName: !!trimmedName
        })
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast({
        title: "Something went wrong",
        description: "Please try again or contact support if the issue persists.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubscribed) {
    return (
      <Card className={`${className} border-green-200 bg-green-50`}>
        <CardContent className="p-6 text-center">
          <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-green-800 mb-2">You're all set!</h3>
          <p className="text-green-700">
            Thanks for subscribing! We'll send you the {currentMagnet.title.toLowerCase()} soon.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (variant === 'hero') {
    return (
      <Card className={`${className} max-w-md mx-auto`}>
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-2">
            <currentMagnet.icon className="h-8 w-8 text-primary mr-2" />
            <Badge variant="secondary">Free Download</Badge>
          </div>
          <CardTitle className="text-xl">
            {title || currentMagnet.title}
          </CardTitle>
          <CardDescription>
            {description || currentMagnet.description}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="text"
              placeholder="Your name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>Loading...</>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Get Free Download
                </>
              )}
            </Button>
          </form>
          <p className="text-xs text-muted-foreground mt-3 text-center">
            We respect your privacy. Unsubscribe at any time.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (variant === 'sidebar') {
    return (
      <Card className={`${className}`}>
        <CardHeader>
          <CardTitle className="text-lg flex items-center">
            <Gift className="h-5 w-5 mr-2 text-primary" />
            Free Resource
          </CardTitle>
          <CardDescription className="text-sm">
            {currentMagnet.description}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-3">
            <Input
              type="email"
              placeholder="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="text-sm"
            />
            <Button type="submit" size="sm" className="w-full" disabled={isLoading}>
              {isLoading ? 'Sending...' : 'Download Free'}
            </Button>
          </form>
        </CardContent>
      </Card>
    );
  }

  // Default variant
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>
          {title || "Stay Updated"}
        </CardTitle>
        <CardDescription>
          {description || "Get the latest tips and resources delivered to your inbox."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="flex gap-2">
          <Input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="flex-1"
          />
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Subscribing...' : 'Subscribe'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};